// OfficeProbePanel.jsx - bagian "Indomas Office" di AWS Inventory → Network Tunnels.
// Status device LAN kantor Indomas (10.32.0.0/23) yang di-probe lewat agen di EC2 Jakarta.
// Data dari backend/officeProbe.js (cron office_probe). Teks literal Inggris (bukan i18n).
import React, { useCallback, useEffect, useState } from 'react';
import { Building2, RefreshCw, Plus, Pencil, Trash2, Settings, X, Save, Bell, BellOff, Radar } from 'lucide-react';
import { api, useAuth } from '../context/AuthContext';
import { allowedLocs, locOk } from '../utils/siteScope';

const STATUS = {
  up:       { label: 'Up',       cls: 'bg-emerald-50 text-emerald-700 ring-emerald-200', dot: '#10b981' },
  degraded: { label: 'Degraded', cls: 'bg-amber-50 text-amber-700 ring-amber-200',       dot: '#f59e0b' },
  down:     { label: 'Down',     cls: 'bg-rose-50 text-rose-700 ring-rose-200',          dot: '#e11d48' },
  error:    { label: 'Error',    cls: 'bg-slate-100 text-slate-700 ring-slate-200',      dot: '#64748b' },
};
const RANGES = [[6, '6h'], [24, '24h'], [24 * 7, '7d'], [24 * 30, '30d']];

function Badge({ status }) {
  const s = STATUS[status] || { label: 'No data', cls: 'bg-slate-50 text-slate-500 ring-slate-200' };
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ring-1 ${s.cls}`}>{s.label}</span>;
}

// Strip riwayat: satu batang per hasil (maks 120 terakhir).
function HistoryStrip({ rows }) {
  const r = (rows || []).slice(-120);
  if (!r.length) return <span className="text-xs text-slate-400">-</span>;
  return <div className="flex items-end gap-px h-5" title={`${r.length} checks`}>
    {r.map((x, i) => <div key={i} className="w-1 rounded-sm" style={{ height: '100%', background: (STATUS[x.status] || STATUS.error).dot }}
      title={`${x.checked_at} UTC · ${x.status}${x.rtt_ms != null ? ` · ${x.rtt_ms} ms` : ''}`} />)}
  </div>;
}

const checksText = (cs) => (cs || []).map((k) => k.type === 'tcp' ? `tcp/${k.port}` : k.type).join(', ');

function parseChecksInput(txt, community) {
  // contoh: "ping, tcp 53, tcp 389, snmp"
  const out = [];
  for (const raw of String(txt || '').split(/[,;]+/)) {
    const t = raw.trim().toLowerCase();
    if (!t) continue;
    if (t === 'ping') out.push({ type: 'ping' });
    else if (t.startsWith('snmp')) out.push({ type: 'snmp', community: community || 'public' });
    else {
      const m = t.match(/^(?:tcp[\s/:]*)?(\d{1,5})$/);
      if (m) out.push({ type: 'tcp', port: Number(m[1]) });
      else throw new Error(`Unknown check "${raw.trim()}" (use: ping, tcp 389, snmp)`);
    }
  }
  return out.length ? out : [{ type: 'ping' }];
}

function DeviceForm({ initial, defaultSite, onClose, onSaved }) {
  const snmp = (initial?.checks || []).find((k) => k.type === 'snmp');
  const [f, setF] = useState({
    site: initial?.site || defaultSite || 'Jakarta',
    name: initial?.name || '', host: initial?.host || '',
    checks: initial ? checksText(initial.checks).replace(/tcp\//g, 'tcp ') : 'ping',
    community: snmp?.community || '', enabled: initial ? !!initial.enabled : true,
    sort_order: initial?.sort_order || 0,
  });
  const [err, setErr] = useState('');
  const save = async () => {
    try {
      setErr('');
      const body = { site: f.site, name: f.name, host: f.host, checks: parseChecksInput(f.checks, f.community), enabled: f.enabled, sort_order: Number(f.sort_order) || 0 };
      if (initial?.id) await api.put(`/api/net/office/devices/${initial.id}`, body);
      else await api.post('/api/net/office/devices', body);
      onSaved();
    } catch (e) { setErr(e.response?.data?.error || e.message); }
  };
  const inp = 'w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200';
  return <div className="fixed inset-0 z-50 bg-black/30 flex items-center justify-center p-4" onClick={onClose}>
    <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-5 space-y-3" onClick={(e) => e.stopPropagation()}>
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-slate-800">{initial?.id ? 'Edit device' : 'Add device'}</h3>
        <button onClick={onClose} className="p-1 rounded hover:bg-slate-100"><X size={16} /></button>
      </div>
      <label className="block text-xs text-slate-500">Name<input className={inp} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></label>
      <label className="block text-xs text-slate-500">Site<select className={inp} value={f.site} onChange={(e) => setF({ ...f, site: e.target.value })}>
        <option value="Jakarta">Jakarta (10.32.0.0/23 · via agent)</option><option value="Medan">Medan (10.26.0.0/16 · direct, ICMP blocked)</option><option value="Dumai">Dumai (10.43.0.0/16, 10.32.30.0/23 · direct)</option><option value="Mempawah">Mempawah / PBI (10.38.0.0/23, 10.35.50.0/23 · direct)</option></select></label>
      <label className="block text-xs text-slate-500">IP address<input className={inp} value={f.host} onChange={(e) => setF({ ...f, host: e.target.value })} placeholder="10.32.0.11" /></label>
      <label className="block text-xs text-slate-500">Checks<input className={inp} value={f.checks} onChange={(e) => setF({ ...f, checks: e.target.value })} placeholder="ping, tcp 53, tcp 389, snmp" /></label>
      {/snmp/i.test(f.checks) && <label className="block text-xs text-slate-500">SNMP community (v2c)<input className={inp} value={f.community} onChange={(e) => setF({ ...f, community: e.target.value })} /></label>}
      <div className="flex gap-3">
        <label className="block text-xs text-slate-500 w-24">Order<input type="number" className={inp} value={f.sort_order} onChange={(e) => setF({ ...f, sort_order: e.target.value })} /></label>
        <label className="flex items-center gap-2 text-sm text-slate-600 mt-5"><input type="checkbox" checked={f.enabled} onChange={(e) => setF({ ...f, enabled: e.target.checked })} /> Enabled</label>
      </div>
      {err && <div className="text-xs text-rose-600">{err}</div>}
      <div className="flex justify-end gap-2 pt-1">
        <button onClick={onClose} className="px-3 py-2 rounded-lg text-sm text-slate-600 hover:bg-slate-100">Cancel</button>
        <button onClick={save} className="px-3 py-2 rounded-lg text-sm bg-blue-600 text-white hover:bg-blue-700 inline-flex items-center gap-1.5"><Save size={14} /> Save</button>
      </div>
    </div>
  </div>;
}

function SettingsForm({ config, onClose, onSaved }) {
  const [f, setF] = useState({ agent_url: config?.agent_url || '', agent_token: '', bot: config?.bot || 'notify', alert: !!config?.alert });
  const [err, setErr] = useState('');
  const [health, setHealth] = useState('');
  const save = async () => {
    try { setErr(''); await api.put('/api/net/office/config', f); onSaved(); }
    catch (e) { setErr(e.response?.data?.error || e.message); }
  };
  const test = async () => {
    try { setHealth('...'); const r = await api.get('/api/net/office/agent-health'); setHealth(`OK · ${r.data.agent} · allow ${(r.data.allow || []).join(', ')}`); }
    catch (e) { setHealth('FAIL · ' + (e.response?.data?.error || e.message)); }
  };
  const inp = 'w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200';
  return <div className="fixed inset-0 z-50 bg-black/30 flex items-center justify-center p-4" onClick={onClose}>
    <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-5 space-y-3" onClick={(e) => e.stopPropagation()}>
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-slate-800">Probe agent settings</h3>
        <button onClick={onClose} className="p-1 rounded hover:bg-slate-100"><X size={16} /></button>
      </div>
      <label className="block text-xs text-slate-500">Agent URL<input className={inp} value={f.agent_url} onChange={(e) => setF({ ...f, agent_url: e.target.value })} /></label>
      <label className="block text-xs text-slate-500">Agent token {config?.has_token ? '(saved — leave blank to keep)' : '(not set)'}
        <input type="password" className={inp} value={f.agent_token} onChange={(e) => setF({ ...f, agent_token: e.target.value })} /></label>
      <div className="flex gap-3 items-end">
        <label className="block text-xs text-slate-500 flex-1">Telegram bot key<input className={inp} value={f.bot} onChange={(e) => setF({ ...f, bot: e.target.value })} /></label>
        <label className="flex items-center gap-2 text-sm text-slate-600 pb-2"><input type="checkbox" checked={f.alert} onChange={(e) => setF({ ...f, alert: e.target.checked })} /> Alert on change</label>
      </div>
      <div className="text-xs text-slate-500">
        <button onClick={test} className="underline text-blue-600">Test agent</button> {health && <span className="ml-1">{health}</span>}
      </div>
      {err && <div className="text-xs text-rose-600">{err}</div>}
      <div className="flex justify-end gap-2 pt-1">
        <button onClick={onClose} className="px-3 py-2 rounded-lg text-sm text-slate-600 hover:bg-slate-100">Cancel</button>
        <button onClick={save} className="px-3 py-2 rounded-lg text-sm bg-blue-600 text-white hover:bg-blue-700 inline-flex items-center gap-1.5"><Save size={14} /> Save</button>
      </div>
    </div>
  </div>;
}

// Waktu UTC dari DB ("YYYY-MM-DD HH:MM:SS") → tampil WIB.
const toWib = (s) => {
  if (!s) return '';
  const d = new Date(s.replace(' ', 'T') + 'Z');
  return d.toLocaleString('en-GB', { timeZone: 'Asia/Jakarta', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
};
const fmtDur = (m) => m < 60 ? `${m} min` : m < 1440 ? `${Math.floor(m / 60)}h ${m % 60}m` : `${Math.floor(m / 1440)}d ${Math.floor((m % 1440) / 60)}h`;

function EventLog({ devices, reloadKey }) {
  const [days, setDays] = useState(7);
  const [dev, setDev] = useState('');
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    api.get('/api/net/office/events', { params: dev ? { days, device_id: dev } : { days } })
      .then((r) => { setData(r.data); setErr(''); })
      .catch((e) => setErr(e.response?.data?.error || e.message));
  }, [days, dev, reloadKey]);
  const ev = data?.events || [];
  return <div className="pt-2 border-t border-slate-100 space-y-3">
    <div className="flex flex-wrap items-center gap-2">
      <div className="font-semibold text-slate-800 text-sm">Event log (disconnects)</div>
      <span className="text-xs text-slate-500">{ev.length} event{ev.length === 1 ? '' : 's'} · times in WIB</span>
      <div className="ml-auto flex items-center gap-2">
        <select value={dev} onChange={(e) => setDev(e.target.value)} className="px-2 py-1.5 rounded-lg border border-slate-200 text-xs">
          <option value="">All devices</option>
          {(devices || []).map((d) => <option key={d.id} value={d.id}>{d.name} ({d.host})</option>)}
        </select>
        <div className="flex bg-slate-100 rounded-xl p-1">
          {[[1, '24h'], [7, '7d'], [30, '30d'], [90, '90d']].map(([v, l]) => <button key={v} onClick={() => setDays(v)}
            className={`px-2.5 py-1 rounded-lg text-xs font-medium ${days === v ? 'bg-white shadow text-slate-800' : 'text-slate-500'}`}>{l}</button>)}
        </div>
      </div>
    </div>
    {err && <div className="text-sm text-rose-600">{err}</div>}
    {!ev.length ? <div className="text-sm text-emerald-700">No disconnects recorded in this period.</div> :
      <div className="overflow-x-auto max-h-96 overflow-y-auto">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-white"><tr className="text-left text-xs text-slate-500 border-b border-slate-100">
            <th className="py-2 pr-3">Device</th><th className="py-2 pr-3">IP</th><th className="py-2 pr-3">Status</th>
            <th className="py-2 pr-3">Started</th><th className="py-2 pr-3">Recovered</th><th className="py-2 pr-3">Duration</th><th className="py-2 pr-3">Detail</th></tr></thead>
          <tbody>
            {ev.map((e, i) => <tr key={i} className="border-b border-slate-50">
              <td className="py-2 pr-3 font-medium text-slate-800">{e.name}</td>
              <td className="py-2 pr-3 font-mono text-xs">{e.host}</td>
              <td className="py-2 pr-3"><Badge status={e.status} /></td>
              <td className="py-2 pr-3 text-xs">{toWib(e.start)}</td>
              <td className="py-2 pr-3 text-xs">{e.ongoing ? <span className="text-rose-600 font-medium">ongoing</span> : toWib(e.end)}</td>
              <td className="py-2 pr-3 text-xs">{fmtDur(e.duration_min)}</td>
              <td className="py-2 pr-3 text-[11px] text-slate-500 max-w-md truncate" title={e.detail}>{e.detail}</td>
            </tr>)}
          </tbody>
        </table>
      </div>}
    <div className="text-[11px] text-slate-400">An event starts at the first non-Up check and ends at the next Up check (5-minute resolution). "Error" means the probe agent itself was unreachable.</div>
  </div>;
}

// Aset SDP Cloud (region Jakarta) jenis server/jaringan yang IP-nya belum dimonitor.
function SdpCandidates({ site, onAdded, reloadKey }) {
  const [list, setList] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState('');
  const load = useCallback(() => {
    api.get('/api/net/office/sdp-candidates', { params: { site } }).then((r) => setList(r.data.candidates || [])).catch(() => setList([]));
  }, [site]);
  useEffect(() => { load(); }, [load, reloadKey]);
  if (!list || !list.length) return null;
  const add = async (a) => {
    setBusy(a.ip);
    try {
      await api.post('/api/net/office/devices', { site, name: String(a.name || a.ip).replace(/\.id\.hsagroup\.com$/i, ''), host: a.ip, checks: site === 'Medan' ? [{ type: 'tcp', port: 3389 }, { type: 'tcp', port: 445 }] : (site === 'Dumai' || site === 'Mempawah') ? [{ type: 'ping' }, { type: 'tcp', port: 3389 }] : [{ type: 'ping' }], enabled: true, sort_order: 100 });
      onAdded(); load();
    } finally { setBusy(''); }
  };
  return <div className="rounded-xl border border-dashed border-slate-200 p-3">
    <button onClick={() => setOpen(!open)} className="text-sm font-medium text-slate-700">
      {open ? '▾' : '▸'} {list.length} SDP asset{list.length === 1 ? '' : 's'} ({site}) not monitored yet
    </button>
    {open && <table className="w-full text-xs mt-2">
      <tbody>{list.map((a) => <tr key={a.ip} className="border-t border-slate-50">
        <td className="py-1.5 pr-3 font-mono">{a.ip}</td>
        <td className="py-1.5 pr-3 font-medium text-slate-700">{a.name}</td>
        <td className="py-1.5 pr-3 text-slate-500">{a.product_type} · {a.product_name}</td>
        <td className="py-1.5 pr-3 text-slate-500">{a.state}</td>
        <td className="py-1.5 text-right"><button disabled={busy === a.ip} onClick={() => add(a)}
          className="px-2 py-1 rounded-lg border border-slate-200 hover:bg-slate-50 inline-flex items-center gap-1"><Plus size={12} /> Add ({site === 'Medan' ? 'tcp 3389, 445' : (site === 'Dumai' || site === 'Mempawah') ? 'ping, tcp 3389' : 'ping'})</button></td>
      </tr>)}</tbody>
    </table>}
  </div>;
}

// Discover: scan subnet site, tampilkan host yang menjawab tapi belum dimonitor.
const INFRA_PORTS = [22, 53, 80, 443, 554, 8080, 9100, 10050];
function DiscoverModal({ site, onClose, onAdded }) {
  const [job, setJob] = useState(null);
  const [err, setErr] = useState('');
  const [hidePc, setHidePc] = useState(true);
  const [added, setAdded] = useState({});
  useEffect(() => {
    let t;
    const poll = () => api.get('/api/net/office/discover', { params: { site } }).then((r) => {
      setJob(r.data.job); if (r.data.job && r.data.job.running) t = setTimeout(poll, 3000);
    }).catch((e) => setErr(e.response?.data?.error || e.message));
    api.post('/api/net/office/discover', null, { params: { site } }).then((r) => { setJob(r.data.job); t = setTimeout(poll, 2000); })
      .catch((e) => setErr(e.response?.data?.error || e.message));
    return () => clearTimeout(t);
  }, [site]);
  const add = async (h) => {
    const checks = [];
    if (h.ping && site !== 'Medan') checks.push({ type: 'ping' });
    const pick = h.ports.filter((p) => [53, 445, 3389, 554, 9100, 10050].includes(p)).slice(0, 3);
    (pick.length ? pick : h.ports.slice(0, 2)).forEach((p) => checks.push({ type: 'tcp', port: p }));
    if (!checks.length) checks.push({ type: 'ping' });
    const name = h.asset ? String(h.asset.name).replace(/\.(id\.hsagroup\.com|pimid\.hsagroup\.com|local)$/i, '') : `${site} ${h.ip}`;
    try { await api.post('/api/net/office/devices', { site, name, host: h.ip, checks, enabled: true, sort_order: 500 }); setAdded((a) => ({ ...a, [h.ip]: true })); onAdded(); }
    catch (e) { setErr(e.response?.data?.error || e.message); }
  };
  const hosts = (job?.hosts || []).filter((h) => !hidePc || !h.likely_pc);
  return <div className="fixed inset-0 z-50 bg-black/30 flex items-center justify-center p-4" onClick={onClose}>
    <div className="bg-white rounded-2xl shadow-xl w-full max-w-4xl p-5 space-y-3 max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
      <div className="flex items-center gap-2">
        <Radar size={16} className="text-blue-600" /><h3 className="font-semibold text-slate-800">Discover · {site}</h3>
        <span className="text-xs text-slate-500">{job ? (job.running ? `scanning… ${job.progress || ''}` : (job.error ? `error: ${job.error}` : job.progress)) : 'starting…'}</span>
        <label className="ml-auto flex items-center gap-1.5 text-xs text-slate-600"><input type="checkbox" checked={hidePc} onChange={(e) => setHidePc(e.target.checked)} /> Hide likely PCs</label>
        <button onClick={onClose} className="p-1 rounded hover:bg-slate-100"><X size={16} /></button>
      </div>
      {err && <div className="text-xs text-rose-600">{err}</div>}
      <div className="overflow-auto">
        {job && !job.running && !hosts.length ? <div className="text-sm text-slate-500 py-6 text-center">No new hosts found.</div> :
        <table className="w-full text-xs">
          <thead className="sticky top-0 bg-white"><tr className="text-left text-slate-500 border-b border-slate-100">
            <th className="py-1.5 pr-3">IP</th><th className="py-1.5 pr-3">Ping</th><th className="py-1.5 pr-3">Open ports</th><th className="py-1.5 pr-3">SDP</th><th /></tr></thead>
          <tbody>{hosts.map((h) => <tr key={h.ip} className="border-b border-slate-50">
            <td className="py-1.5 pr-3 font-mono">{h.ip}</td>
            <td className="py-1.5 pr-3">{h.ping ? 'yes' : '-'}</td>
            <td className="py-1.5 pr-3">{h.ports.map((p) => <span key={p} className={`inline-block mr-1 px-1.5 rounded ${INFRA_PORTS.includes(p) ? 'bg-blue-50 text-blue-700' : 'bg-slate-100 text-slate-600'}`}>{p}</span>)}</td>
            <td className="py-1.5 pr-3 text-slate-600">{h.asset ? `${h.asset.name} · ${h.asset.product_type || ''}` : '-'}{h.likely_pc && <span className="ml-1 text-slate-400">(likely PC)</span>}</td>
            <td className="py-1.5 text-right">{added[h.ip] ? <span className="text-emerald-600">added</span> :
              <button onClick={() => add(h)} className="px-2 py-1 rounded-lg border border-slate-200 hover:bg-slate-50 inline-flex items-center gap-1"><Plus size={12} /> Add</button>}</td>
          </tr>)}</tbody>
        </table>}
      </div>
      <div className="text-[11px] text-slate-400">Scans the site's server subnets (ping + common TCP ports). Hosts already monitored are hidden. Jakarta goes through the probe agent and takes ~3 minutes.</div>
    </div>
  </div>;
}

// Device grouping by SDP Cloud product type (network devices first).
const NET_TYPES = ['Firewall', 'Router', 'Switch Non IT', 'Switch', 'Access Point Non IT', 'Access Point'];
const TYPE_ORDER = [...NET_TYPES, 'Server', 'Virtual Host', 'Virtual Machine', 'NAS', 'Workstation', 'Computer',
  'CCTV DVR/NVR', 'CCTV', 'CCTV POE', 'Printer Non IT', 'Scanner', 'UPS', 'PABX', 'Attendance Machine',
  'Audio & Camera Communication Non IT', 'Projector'];
const OTHER_TYPE = 'Other (not in SDP)';
// Fallback for devices without a matching SDP asset: guess the SDP product type from the device name.
function inferProductType(name) {
  const n = String(name || '');
  if (/watchguard|fortigate|firewall|sophos|pfsense|palo ?alto/i.test(n)) return 'Firewall';
  if (/router|mikrotik/i.test(n)) return 'Router';
  if (/switch/i.test(n)) return 'Switch Non IT';
  if (/unifi|access ?point|\bap\b|ruckus|aruba/i.test(n)) return 'Access Point Non IT';
  if (/\bnvr\b|\bdvr\b/i.test(n)) return 'CCTV DVR/NVR';
  if (/cctv|camera/i.test(n)) return 'CCTV';
  if (/printer|docucentre|laserjet|epson/i.test(n)) return 'Printer Non IT';
  if (/\bnas\b|synology|qnap/i.test(n)) return 'NAS';
  if (/\bups\b/i.test(n)) return 'UPS';
  if (/pabx/i.test(n)) return 'PABX';
  return null;
}
function groupDevices(devs) {
  const map = new Map();
  for (const d of devs) {
    const sdpType = d.asset?.product_type || null;
    const guess = sdpType ? null : inferProductType(d.name);
    const type = sdpType || guess || OTHER_TYPE;
    d._ptInferred = !sdpType && !!guess;
    if (!map.has(type)) map.set(type, []);
    map.get(type).push(d);
  }
  const rank = (t) => { const i = TYPE_ORDER.indexOf(t); return t === OTHER_TYPE ? 9999 : i >= 0 ? i : 500; };
  return [...map.entries()]
    .map(([type, items]) => ({ key: type, label: type, net: NET_TYPES.includes(type), items }))
    .sort((a, b) => rank(a.key) - rank(b.key) || a.key.localeCompare(b.key));
}

export default function OfficeProbePanel() {
  const [data, setData] = useState(null);
  const [hours, setHours] = useState(24);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [edit, setEdit] = useState(null);      // null | {} (baru) | device
  const [showCfg, setShowCfg] = useState(false);
  const { user } = useAuth();
  const locs = allowedLocs(user);
  const siteList = ['Jakarta', 'Medan', 'Dumai', 'Mempawah'].filter((st) => locOk(locs, st));
  const [site, setSite] = useState(siteList[0] || 'Jakarta');
  const [disc, setDisc] = useState(false);
  const [syncMsg, setSyncMsg] = useState('');
  const [collapsed, setCollapsed] = useState({});

  const load = useCallback(() => {
    api.get('/api/net/office/status', { params: { hours } })
      .then((r) => { setData(r.data); setErr(''); })
      .catch((e) => setErr(e.response?.data?.error || e.message));
  }, [hours]);
  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, [load]);

  const checkNow = () => {
    setBusy(true);
    api.post('/api/net/office/check').then(load).catch((e) => setErr(e.response?.data?.error || e.message))
      .finally(() => setBusy(false));
  };
  const syncSdp = async () => {
    try { const r = await api.post('/api/net/office/sdp-sync'); setSyncMsg(`SDP sync: ${r.data.renamed} renamed, ${r.data.moved} IP change(s)`); load(); }
    catch (e) { setErr(e.response?.data?.error || e.message); }
  };
  const useSdpIp = async (d) => {
    if (!window.confirm(`Change ${d.name} from ${d.host} to ${d.sdp_new_ip} (from SDP)?`)) return;
    try { await api.post(`/api/net/office/devices/${d.id}/use-sdp-ip`); load(); } catch (e) { setErr(e.response?.data?.error || e.message); }
  };
  const toggleAlert = async (d) => {
    try { await api.put(`/api/net/office/devices/${d.id}/alert`, { alert: d.alert === 0 }); load(); }
    catch (e) { setErr(e.response?.data?.error || e.message); }
  };
  const remove = async (d) => {
    if (!window.confirm(`Delete ${d.name} (${d.host}) and its history?`)) return;
    try { await api.delete(`/api/net/office/devices/${d.id}`); load(); } catch (e) { setErr(e.response?.data?.error || e.message); }
  };

  const allDevs = data?.devices || [];
  const devs = allDevs.filter((d) => (d.site || 'Jakarta') === site);
  const groups = groupDevices(devs);
  const cnt = (s) => devs.filter((d) => d.enabled && d.latest?.status === s).length;

  return <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-4">
    <div className="flex flex-wrap items-center gap-3">
      <Building2 size={18} className="text-blue-600" />
      <div>
        <div className="font-semibold text-slate-800">Site devices</div>
        <div className="text-xs text-slate-500">{site === 'Medan' ? 'Medan 10.26.0.0/16 · probed directly from dashboard (TCP; ICMP blocked at site)' : site === 'Dumai' ? 'Dumai 10.43.0.0/16 + 10.32.30.0/23 · probed directly from dashboard (ping + TCP)' : site === 'Mempawah' ? 'Mempawah (PBI) 10.38.0.0/23 + 10.35.50.0/23 · probed directly from dashboard (ping + TCP)' : <>Jakarta LAN 10.32.0.0/23 · probed via agent {data?.config?.agent_url || '-'}</>}
          {data?.config?.agent_state === 'down' && <span className="ml-2 text-rose-600 font-medium">agent unreachable</span>}
          {data && !data.config?.has_token && <span className="ml-2 text-amber-600 font-medium">agent token not set</span>}
        </div>
      </div>
      <div className="flex bg-slate-100 rounded-xl p-1 ml-2">
        {siteList.map((st) => <button key={st === "Mempawah" ? "Kijing (Mempawah)" : st} onClick={() => setSite(st)}
          className={`px-3 py-1 rounded-lg text-xs font-semibold ${site === st ? 'bg-white shadow text-blue-700' : 'text-slate-500'}`}>
          {st} <span className="text-slate-400 font-normal">{allDevs.filter((d) => (d.site || 'Jakarta') === st).length}</span></button>)}
      </div>
      <div className="ml-auto flex items-center gap-2">
        <div className="flex bg-slate-100 rounded-xl p-1">
          {RANGES.map(([h, l]) => <button key={h} onClick={() => setHours(h)}
            className={`px-2.5 py-1 rounded-lg text-xs font-medium ${hours === h ? 'bg-white shadow text-slate-800' : 'text-slate-500'}`}>{l}</button>)}
        </div>
        {data?.can_edit !== false && <>
        <button onClick={() => setShowCfg(true)} className="p-2 rounded-lg hover:bg-slate-100 text-slate-600" title="Settings"><Settings size={16} /></button>
        <button onClick={syncSdp} className="px-3 py-2 rounded-lg text-sm border border-slate-200 hover:bg-slate-50" title={`Sync names/IPs with SDP Assets${data?.sdp_synced_at ? ' · last ' + data.sdp_synced_at : ''}`}>Sync SDP</button>
        <button onClick={() => setDisc(true)} className="px-3 py-2 rounded-lg text-sm border border-slate-200 hover:bg-slate-50 inline-flex items-center gap-1.5"><Radar size={14} /> Discover</button>
        <button onClick={() => setEdit({})} className="px-3 py-2 rounded-lg text-sm border border-slate-200 hover:bg-slate-50 inline-flex items-center gap-1.5"><Plus size={14} /> Add</button>
        <button onClick={checkNow} disabled={busy} className="px-3 py-2 rounded-lg text-sm bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60 inline-flex items-center gap-1.5">
          <RefreshCw size={14} className={busy ? 'animate-spin' : ''} /> Check now</button>
        </>}
      </div>
    </div>

    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
      {[['Devices', devs.filter((d) => d.enabled).length, 'text-slate-800'], ['Up', cnt('up'), 'text-emerald-600'],
        ['Degraded', cnt('degraded'), 'text-amber-600'], ['Down / error', cnt('down') + cnt('error'), 'text-rose-600']].map(([l, v, c]) =>
        <div key={l} className="rounded-xl bg-slate-50 px-4 py-3"><div className="text-xs text-slate-500">{l}</div><div className={`text-xl font-semibold ${c}`}>{v}</div></div>)}
    </div>

    {err && <div className="text-sm text-rose-600">{err}</div>}
    {syncMsg && <div className="text-xs text-slate-500">{syncMsg}</div>}

    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead><tr className="text-left text-xs text-slate-500 border-b border-slate-100">
          <th className="py-2 pr-3">Device</th><th className="py-2 pr-3">IP</th><th className="py-2 pr-3">Checks</th>
          <th className="py-2 pr-3">Status</th><th className="py-2 pr-3">RTT</th><th className="py-2 pr-3">Uptime</th>
          <th className="py-2 pr-3">History</th><th className="py-2 pr-3">Last check (UTC)</th><th className="py-2 pr-3">Alert</th><th /></tr></thead>
        <tbody>
          {groups.map((g) => <React.Fragment key={g.key}>
              <tr className="bg-slate-50 border-b border-slate-100">
                <td colSpan={10} className="py-1.5 px-2">
                  <button onClick={() => setCollapsed((c) => ({ ...c, [g.key]: !c[g.key] }))} className="flex items-center gap-2 text-xs font-semibold text-slate-700 w-full text-left">
                    <span className="text-slate-400 w-3">{collapsed[g.key] ? '▸' : '▾'}</span>
                    <span>{g.label}</span>
                    {g.net && <span className="px-1.5 py-0.5 rounded-md bg-blue-50 text-blue-700 ring-1 ring-blue-200 text-[10px] font-medium">Network</span>}
                    <span className="text-slate-400 font-normal">{g.items.length}</span>
                    {(() => { const c = (s) => g.items.filter((d) => d.enabled && d.latest?.status === s).length; const dn = c('down') + c('error'); return <span className="ml-auto flex gap-3 font-normal">
                      <span className="text-emerald-600">{c('up')} up</span>
                      {c('degraded') > 0 && <span className="text-amber-600">{c('degraded')} degraded</span>}
                      {dn > 0 && <span className="text-rose-600">{dn} down</span>}
                    </span>; })()}
                  </button>
                </td>
              </tr>
              {!collapsed[g.key] && g.items.map((d) => <tr key={d.id} className={`border-b border-slate-50 ${d.enabled ? '' : 'opacity-50'}`}>
            <td className="py-2 pr-3">
              <div className="font-medium text-slate-800">{d.name}</div>
              {d._ptInferred && <div className="text-[11px] text-slate-400" title="No matching SDP asset; product type guessed from the device name">Not in SDP · type inferred from name</div>}
              {d.asset && <div className="text-[11px] text-slate-500" title={`SDP asset · ${d.asset.product_type || ''} · SN ${d.asset.serial_number || '-'} · MAC ${d.asset.mac_address || '-'}${d.asset.others ? ` · +${d.asset.others} other asset(s) with this IP` : ''}`}>
                SDP: {d.asset.name} · {d.asset.product_name || d.asset.product_type}
                {d.asset.state && d.asset.state !== 'In Use' && <span className="ml-1 text-amber-600">({d.asset.state})</span>}
              </div>}
              {d.sdp_new_ip && <div className="text-[11px] text-amber-700">SDP now lists this asset at {d.sdp_new_ip}
                <button onClick={() => useSdpIp(d)} className="ml-1 underline">use new IP</button></div>}
            </td>
            <td className="py-2 pr-3 font-mono text-xs">{d.host}</td>
            <td className="py-2 pr-3 text-xs text-slate-600">{checksText(d.checks)}</td>
            <td className="py-2 pr-3"><Badge status={d.latest?.status} />
              {d.latest?.detail && d.latest.status !== 'up' && <div className="text-[11px] text-slate-500 mt-0.5 max-w-xs truncate" title={d.latest.detail}>{d.latest.detail}</div>}</td>
            <td className="py-2 pr-3 text-xs">{d.latest?.rtt_ms != null ? `${d.latest.rtt_ms} ms` : '-'}</td>
            <td className="py-2 pr-3 text-xs">{d.uptime_pct != null ? `${d.uptime_pct}%` : '-'}</td>
            <td className="py-2 pr-3"><HistoryStrip rows={d.history} /></td>
            <td className="py-2 pr-3 text-xs text-slate-500">{d.latest?.checked_at || '-'}</td>
            <td className="py-2 pr-3">
              <button onClick={() => toggleAlert(d)} title={d.alert === 0 ? 'Telegram alert OFF — click to turn on' : 'Telegram alert ON — click to mute'}
                className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-medium ring-1 ${d.alert === 0 ? 'bg-slate-50 text-slate-400 ring-slate-200' : 'bg-blue-50 text-blue-700 ring-blue-200'}`}>
                {d.alert === 0 ? <BellOff size={12} /> : <Bell size={12} />}{d.alert === 0 ? 'Off' : 'On'}</button>
            </td>
            <td className="py-2 text-right whitespace-nowrap">
              <button onClick={() => setEdit(d)} className="p-1.5 rounded hover:bg-slate-100 text-slate-500" title="Edit"><Pencil size={14} /></button>
              <button onClick={() => remove(d)} className="p-1.5 rounded hover:bg-rose-50 text-rose-500" title="Delete"><Trash2 size={14} /></button>
            </td>
          </tr>)}</React.Fragment>)}
          {!devs.length && <tr><td colSpan={10} className="py-6 text-center text-slate-400 text-sm">No devices yet — click Add.</td></tr>}
        </tbody>
      </table>
    </div>
    <div className="text-[11px] text-slate-400">Checks run every 5 minutes (cron office_probe). Status: all checks ok = Up, some = Degraded, none = Down.</div>

    <SdpCandidates site={site} onAdded={load} reloadKey={allDevs.length} />

    <EventLog devices={devs} reloadKey={data} />

    {edit && <DeviceForm initial={edit.id ? edit : null} defaultSite={site} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
    {disc && <DiscoverModal site={site} onClose={() => setDisc(false)} onAdded={load} />}
    {showCfg && <SettingsForm config={data?.config} onClose={() => setShowCfg(false)} onSaved={() => { setShowCfg(false); load(); }} />}
  </div>;
}
